import { useEffect, useState, useCallback } from "react";
import { API_BASE_URL } from "../utils/config";

const token = () => localStorage.getItem("accessToken");

async function apiFetch(url, opts = {}) {
  const res = await fetch(`${API_BASE_URL}${url}`, {
    ...opts,
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${token()}`,
      ...(opts.headers || {}),
    },
  });
  if (!res.ok) {
    const data = await res.json().catch(() => ({}));
    throw new Error(
      data.detail || data.non_field_errors?.[0] || JSON.stringify(data) || "Request failed"
    );
  }
  return res.status === 204 ? null : res.json();
}

const STATUS_LABEL = {
  PENDING: { label: "Menunggu", cls: "bg-yellow-400/20 text-yellow-700 dark:text-yellow-300" },
  APPROVED: { label: "Disetujui", cls: "bg-green-400/20 text-green-700 dark:text-green-300" },
  REJECTED: { label: "Ditolak", cls: "bg-red-400/20 text-red-600 dark:text-red-400" },
};

export default function AdminPanelAssemblies() {
  const [assemblies, setAssemblies] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [statusFilter, setStatusFilter] = useState("PENDING");

  const [expandedId, setExpandedId] = useState(null);
  const [approveNote, setApproveNote] = useState({});
  const [processing, setProcessing] = useState({});
  // Inline confirm — replaces window.confirm, which some mobile browsers
  // never dismiss on tap (hasil meet.txt: the same "tombol ACC ngestuck" bug
  // as the transaction/barang-keluar ACC flows, just not patched here yet).
  // { id, type: "approve" | "reject" } | null
  const [confirmTarget, setConfirmTarget] = useState(null);

  const [role, setRole] = useState(null);
  // Approve/reject used to be gated to superadmin only; admin now has the
  // same ACC rights here as everywhere else (2026-07-26).
  const isAdmin = role === "admin" || role === "superadmin";

  useEffect(() => {
    apiFetch("/api/auth/me/")
      .then((data) => setRole(data?.role || null))
      .catch(() => {});
  }, []);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const url =
        statusFilter && statusFilter !== "ALL"
          ? `/api/panel-assemblies/?status=${statusFilter}`
          : "/api/panel-assemblies/";
      const data = await apiFetch(url);
      setAssemblies(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [statusFilter]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  const approve = async (asm) => {
    const note = approveNote[asm.id] || "";
    setConfirmTarget(null);
    setProcessing((p) => ({ ...p, [asm.id]: "approving" }));
    try {
      await apiFetch(`/api/panel-assemblies/${asm.id}/approve/`, {
        method: "POST",
        body: JSON.stringify({ note }),
      });
      fetchAll();
    } catch (e) {
      alert(`Gagal menyetujui: ${e.message}`);
    } finally {
      setProcessing((p) => ({ ...p, [asm.id]: null }));
    }
  };

  const reject = async (asm) => {
    setConfirmTarget(null);
    setProcessing((p) => ({ ...p, [asm.id]: "rejecting" }));
    try {
      await apiFetch(`/api/panel-assemblies/${asm.id}/reject/`, {
        method: "POST",
        body: JSON.stringify({}),
      });
      fetchAll();
    } catch (e) {
      alert(`Gagal menolak: ${e.message}`);
    } finally {
      setProcessing((p) => ({ ...p, [asm.id]: null }));
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-4">
        <h1 className="text-2xl font-semibold">Approval Rakitan Panel</h1>
        <div className="flex gap-2">
          {["PENDING", "APPROVED", "REJECTED", "ALL"].map((s) => (
            <button
              key={s}
              onClick={() => setStatusFilter(s)}
              className={`text-xs px-3 py-1.5 rounded-lg font-medium transition-colors ${
                statusFilter === s
                  ? "bg-brand-600 text-white"
                  : "border border-white/50 dark:border-white/15 hover:bg-white/20"
              }`}
            >
              {s === "ALL" ? "Semua" : s === "PENDING" ? "Menunggu" : s === "APPROVED" ? "Disetujui" : "Ditolak"}
            </button>
          ))}
        </div>
      </div>

      {error && <p className="text-sm text-red-600 mb-3">{error}</p>}
      {loading && <p className="text-sm text-muted mb-3">Loading...</p>}

      {!loading && assemblies.length === 0 && (
        <p className="text-sm text-muted">Tidak ada rakitan panel.</p>
      )}

      <div className="space-y-4">
        {assemblies.map((asm) => {
          const st = STATUS_LABEL[asm.status] || { label: asm.status, cls: "" };
          const isPending = asm.status === "PENDING";
          const isProc = processing[asm.id];

          return (
            <div key={asm.id} className="glass-card p-4">
              <div className="flex items-start justify-between gap-3">
                <div className="min-w-0 flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="font-semibold">{asm.reference_code}</span>
                    <span className={`text-xs px-1.5 py-0.5 rounded font-medium ${st.cls}`}>
                      {st.label}
                    </span>
                  </div>
                  <p className="text-sm">
                    Panel: <span className="font-medium">{asm.output_item_code} – {asm.output_item_name}</span>
                    <span className="text-muted"> &times; {asm.output_quantity} unit</span>
                  </p>
                  <div className="text-xs text-muted mt-0.5 space-y-0.5">
                    <p>Gudang: {asm.location_name || "-"}</p>
                    <p>Dibuat oleh: {asm.user} &bull; {new Date(asm.created_at).toLocaleString("id-ID")}</p>
                    {asm.template_name && <p>Template: {asm.template_name}</p>}
                    {asm.note && <p>Catatan: {asm.note}</p>}
                    {!isPending && asm.approved_by && (
                      <p>
                        {asm.status === "APPROVED" ? "Disetujui" : "Ditolak"} oleh {asm.approved_by}
                        {asm.approved_at && ` · ${new Date(asm.approved_at).toLocaleString("id-ID")}`}
                      </p>
                    )}
                  </div>
                </div>

                <button
                  onClick={() => setExpandedId(expandedId === asm.id ? null : asm.id)}
                  className="shrink-0 text-xs px-2 py-1 rounded border border-white/50 dark:border-white/15 hover:bg-white/20"
                >
                  {expandedId === asm.id ? "Tutup" : `${asm.components.length} komponen`}
                </button>
              </div>

              {expandedId === asm.id && (
                <div className="mt-3 glass-table-wrap overflow-x-auto">
                  <table className="glass-table text-xs">
                    <thead>
                      <tr>
                        <th className="text-left">Kode</th>
                        <th className="text-left">Komponen</th>
                        <th className="text-right">Qty/panel</th>
                        <th className="text-right">Total dibutuhkan</th>
                      </tr>
                    </thead>
                    <tbody>
                      {asm.components.map((c) => (
                        <tr key={c.id}>
                          <td className="px-3 py-1.5">{c.item_code}</td>
                          <td className="px-3 py-1.5">{c.item_name}</td>
                          <td className="px-3 py-1.5 text-right">{c.quantity}</td>
                          <td className="px-3 py-1.5 text-right font-medium">
                            {c.quantity * asm.output_quantity}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {isPending && isAdmin && (
                <div className="mt-3 pt-3 border-t border-white/20 dark:border-white/10">
                  <div className="flex gap-2 items-end">
                    <div className="flex-1">
                      <label className="block text-xs font-medium mb-1">
                        Catatan QC (opsional)
                      </label>
                      <input
                        type="text"
                        className="w-full rounded-md border border-white/50 dark:border-white/15 px-3 py-1.5 text-sm"
                        placeholder="Catatan untuk rakitan ini..."
                        value={approveNote[asm.id] || ""}
                        onChange={(e) =>
                          setApproveNote((p) => ({ ...p, [asm.id]: e.target.value }))
                        }
                      />
                    </div>
                    <button
                      disabled={!!isProc}
                      onClick={() => setConfirmTarget({ id: asm.id, type: "approve" })}
                      className="px-4 py-1.5 rounded-lg bg-green-600 text-white text-sm font-semibold hover:bg-green-700 disabled:opacity-50 shrink-0"
                    >
                      {isProc === "approving" ? "Menyetujui..." : "Setujui"}
                    </button>
                    <button
                      disabled={!!isProc}
                      onClick={() => setConfirmTarget({ id: asm.id, type: "reject" })}
                      className="px-4 py-1.5 rounded-lg bg-red-500 text-white text-sm font-semibold hover:bg-red-600 disabled:opacity-50 shrink-0"
                    >
                      {isProc === "rejecting" ? "Menolak..." : "Tolak"}
                    </button>
                  </div>

                  {confirmTarget?.id === asm.id && (
                    <div className="mt-3 flex flex-wrap items-center gap-3 rounded-md bg-black/5 dark:bg-white/5 px-3 py-2">
                      <p className="text-xs flex-1 min-w-[200px]">
                        {confirmTarget.type === "approve"
                          ? `Setujui rakitan ${asm.reference_code}? Stok komponen akan dikurangi dan panel akan masuk stok.`
                          : `Tolak rakitan ${asm.reference_code}?`}
                      </p>
                      <button
                        type="button"
                        disabled={!!isProc}
                        onClick={() => (confirmTarget.type === "approve" ? approve(asm) : reject(asm))}
                        className={`px-3 py-1.5 rounded-md text-white text-xs font-semibold disabled:opacity-50 ${
                          confirmTarget.type === "approve" ? "bg-green-600 hover:bg-green-700" : "bg-red-600 hover:bg-red-700"
                        }`}
                      >
                        Ya, {confirmTarget.type === "approve" ? "setujui" : "tolak"}
                      </button>
                      <button
                        type="button"
                        onClick={() => setConfirmTarget(null)}
                        className="px-3 py-1.5 rounded-md border border-white/50 dark:border-white/15 text-xs font-semibold"
                      >
                        Batal
                      </button>
                    </div>
                  )}
                </div>
              )}

              {isPending && !isAdmin && role && (
                <div className="mt-3 pt-3 border-t border-white/20 dark:border-white/10">
                  <p className="text-xs text-muted italic">Menunggu approval Admin.</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
